<template>
    <section
        :id="slice.primary.anchorName || defaultAnchorId"
        ref="section"
        class="slice slice-features slice-features-with-media"
        aria-label="features"
        :data-mui-theme="slice.primary.theme"
    >
        <div class="slice-content" :class="{'slice-content--reverse': props.reverse}">
            <div class="slice-features-with-media__wrapper features-media">
                <div class="slice-features-with-media__media">
                    <slot />
                </div>
            </div>
            <div class="slice-features-with-media__wrapper features-content">
                <div class="slice-features-with-media__content">
                    <MuiTag v-if="slice.primary.tag" class="slice-features-with-media__tag">
                        {{ slice.primary.tag }}
                    </MuiTag>
                    <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" />
                    <MuiTextWrapper with-check-marks>
                        <PrismicRichText v-if="slice.primary.description" :field="slice.primary.description" class="slice-description" />
                    </MuiTextWrapper>
                    <MuiLink
                        v-if="cta && cta.url && cta.text"
                        :href="cta.url"
                        :text="cta.text"
                        :target="cta.target"
                        class="slice-features-with-media__cta"
                    />
                    <MktFaqAccordion v-if="faqItems.length > 0" :faq-items="faqItems" class="slice-features-with-media__faq" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import type {RichTextField} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import type {FeaturesSlice} from '@prismicio-types';
    import {MuiTag, MuiLink, MktFaqAccordion, MuiTextWrapper} from '#components';
    import {useLinkHelper} from '#imports';

    type FeaturesWithMediaSlice = Extract<FeaturesSlice, {variation: 'featuresWithDemo' | 'featuresWithImage' | 'featuresWithVideo'}>;

    const props = defineProps<{
        slice: FeaturesWithMediaSlice;
        variationModifier: 'with-demo' | 'with-image' | 'with-video';
        defaultAnchorId: string;
        reverse?: boolean;
    }>();

    const {getLink} = useLinkHelper();
    const cta = computed(() => getLink(props.slice.primary.cta));

    const faqItems = computed(() => {
        type FaqItem = {
            questionTitle: RichTextField;
            answer: RichTextField;
        };

        return (props.slice.primary.faq ?? []) as FaqItem[];
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-features.slice-features-with-media {
        .slice-content {
            text-align: left;

            .slice-features-with-media__tag {
                margin-top: var(--joy-core-spacing-7);
            }

            .slice-title {
                margin-top: var(--joy-core-spacing-8);
            }

            .slice-description,
            .slice-features-with-media__cta {
                margin-bottom: var(--joy-core-spacing-8);
            }

            .slice-features-with-media__faq {
                display: none;
            }

            @include mq.screen_md(min) {
                display: flex;
                justify-content: stretch;

                &--reverse {
                    flex-direction: row-reverse;
                }

                gap: var(--joy-core-spacing-12);

                .slice-features-with-media__tag {
                    margin-top: 0;
                }

                .slice-features-with-media__faq {
                    display: block;
                }

                .slice-features-with-media__wrapper {
                    position: relative;
                }

                .slice-features-with-media__content {
                    flex: 1 1 0%;
                }

                .slice-features-with-media__content,
                .slice-features-with-media__media {
                    position: sticky;
                    top: var(--joy-core-spacing-2);
                }
            }
        }

        .slice-features-with-media__wrapper.features-content {
            width: 100%;
        }
    }
</style>
